import type { ResponseMigrateApproval } from "@/features/migrate/types/api";
import React, { Fragment, useCallback, useMemo, useState } from "react";

import { SupButton, SupListLarge, SupText } from "@repo/core-ui";
import MigrateOrganismTxApprovalAction from "@/features/migrate/ui/Organism/Tx/Approval/Action";

import { useMigrateStore } from "@/features/migrate/state/StoreProvider";

interface MigrateOrganismTxApprovalProps {
  approvals?: ResponseMigrateApproval[];
  chainId: number;
}

export default function MigrateOrganismTxApproval({ approvals, chainId }: MigrateOrganismTxApprovalProps) {
  const { completeApprovals, addCompleteApproval } = useMigrateStore(store => store);
  const [hasStarted, setHasStarted] = useState<boolean>(false);

  const { step, currentApproval, total } = useMemo(() => {
    const step = completeApprovals.length + 1;
    return { step, currentApproval: approvals?.[step - 1], total: approvals?.length ?? 1 };
  }, [approvals, completeApprovals]);

  const handleSuccess = useCallback(
    (completeApproval: ResponseMigrateApproval) => {
      addCompleteApproval(completeApproval);
    },
    [addCompleteApproval]
  );

  return (
    <Fragment>
      <div className="inline-flex w-full flex-col items-stretch justify-between space-y-2 border-[.5px] border-neutral-500 px-4 py-3 pt-2 text-neutral-900">
        <div className="inline-flex items-center justify-start gap-1 self-stretch">
          <SupListLarge className="relative h-6 w-6" />
          <SupText variant="caption" weight="bold" className="leading-3">
            {hasStarted ? `${step - 1} of ${total} APPROVAL${total > 1 ? "S" : ""} COMPLETE` : `${total} APPROVAL${total > 1 ? "S" : ""} NEEDED`}
          </SupText>
        </div>
        <div className="flex w-full items-center justify-stretch bg-neutral-500" style={{ height: "4px" }}>
          <div className="bg-success-600" style={{ width: `${step > 1 ? total / (step - 1) : 0}px`, height: "4px" }} />
        </div>
      </div>
      {hasStarted && currentApproval ? (
        <MigrateOrganismTxApprovalAction approvalData={currentApproval} chainId={chainId} onSuccess={handleSuccess} />
      ) : (
        <SupButton onClickCapture={() => setHasStarted(true)} disabled={hasStarted} fluid>
          Begin
        </SupButton>
      )}
    </Fragment>
  );
}
